import type { PastelColorName } from "./avatar";

import { PASTEL_COLOR_HUES } from "./avatar";

/**
 * Chart colour, drawn from the same hue ring the avatars use so a report reads
 * as part of the app rather than as a charting library dropped into it.
 *
 * The avatar styles light that ring at L 0.93 — a pastel fill sitting behind
 * dark initials. A mark on a white card needs the opposite, so the hues are
 * re-lit here into the band the house semantic tokens already occupy
 * (--accent L 0.62, --success L 0.73, --danger L 0.65): saturated enough to
 * hold a 2px line, dark enough to read against the card.
 */
export const seriesColor = (
  color: PastelColorName | number,
  lightness: number,
  chroma: number,
) =>
  `oklch(${lightness}% ${chroma} ${
    typeof color === "number" ? color : PASTEL_COLOR_HUES[color]
  })`;

/**
 * Named picks, for the charts whose colours are chosen by hand because they
 * carry meaning (delivered vs failed, drafted vs written).
 *
 * Reach for a name here rather than a `--chart-*` step: that ramp is one hue
 * stepped by lightness, so two of its steps side by side are the same colour
 * twice — which is exactly how a four-slice pie ended up as four blues.
 * Judgements still belong to `--success` / `--danger`; these are for the
 * states in between, which need to be *distinct*, not *good* or *bad*.
 */
export const SERIES_COLOR = {
  blue: "var(--accent)",
  purple: seriesColor("purple", 55, 0.17),
  cyan: seriesColor("cyan", 74, 0.11),
  teal: seriesColor(185, 58, 0.11),
  pink: seriesColor(340, 62, 0.16),
  sand: seriesColor(70, 76, 0.09),
  neutral: "var(--series-neutral)",
} as const;

/**
 * The ordinal ramp, for breakdowns that carry no meaning of their own —
 * channel, source, campaign, sender.
 *
 * Cool-led, and stepped by lightness as well as hue, because a dichromat keeps
 * lightness when hue collapses. Red and green stay out of it: a slice must
 * never look like a verdict just because it landed fourth. Orange is out too —
 * at position two it landed on the biggest slice of nearly every breakdown in
 * the app, and a saturated warm shout there reads as a warning about whatever
 * happened to sort first. The one warm left is sand, in the tail.
 *
 * Five hues is the ceiling here, not six: a teal sat 18 degrees off cyan and
 * the two rendered as one colour twice on the booked-by pie. Past five it goes
 * neutral grey and the legend, which always carries the label and the number,
 * does the work.
 */
export const CHART_SERIES = [
  SERIES_COLOR.blue,
  SERIES_COLOR.purple,
  SERIES_COLOR.cyan,
  SERIES_COLOR.pink,
  SERIES_COLOR.sand,
  SERIES_COLOR.neutral,
];

export const chartSeriesColor = (index: number) =>
  CHART_SERIES[index % CHART_SERIES.length];
